import { pushCollectIncident } from '/fp/collect-incidents.js';

const dumpWebGLCore = async (webglContextId, experimentalWebglContextId) => {
    // if (typeof OffscreenCanvas == 'undefined') return null;
    function getWebGLContext() {
        let canvas = document.createElement("canvas");
        canvas.width = 256;
        canvas.height = 256;
        //new OffscreenCanvas(256, 256)
        let result = null;

        try {
            result = canvas.getContext(webglContextId, {preserveDrawingBuffer: false}) || canvas.getContext(experimentalWebglContextId, {preserveDrawingBuffer: false});
        } catch (ex) {}

        result || (result = null);
        return result;
    }

    const webglContext = getWebGLContext();

    if (!webglContext) {
        return {};
    }

    const incidents = [];

    function getMaxAnisotropy(ctx) {
        if (ctx) {
            const ext =
                ctx.getExtension('EXT_texture_filter_anisotropic')
                || ctx.getExtension('WEBKIT_EXT_texture_filter_anisotropic')
                || ctx.getExtension('MOZ_EXT_texture_filter_anisotropic');

            if (ext) {
                return ctx.getParameter(ext.MAX_TEXTURE_MAX_ANISOTROPY_EXT);
            }
        }

        return null;
    }

    // https://registry.khronos.org/webgl/specs/1.0/#5.14.3
    const webgl1Enums = [
        34016,  // ACTIVE_TEXTURE
        33902,  // ALIASED_LINE_WIDTH_RANGE
        33901,  // ALIASED_POINT_SIZE_RANGE
        3413,   // ALPHA_BITS
        34964,  // ARRAY_BUFFER_BINDING
        3042,   // BLEND
        32773,  // BLEND_COLOR
        32970,  // BLEND_DST_ALPHA
        32968,  // BLEND_DST_RGB
        34877,  // BLEND_EQUATION_ALPHA
        32777,  // BLEND_EQUATION_RGB
        32971,  // BLEND_SRC_ALPHA
        32969,  // BLEND_SRC_RGB
        3412,   // BLUE_BITS
        3106,   // COLOR_CLEAR_VALUE
        3107,   // COLOR_WRITEMASK
        34467,  // COMPRESSED_TEXTURE_FORMATS
        2884,   // CULL_FACE
        2885,   // CULL_FACE_MODE
        35725,  // CURRENT_PROGRAM
        3414,   // DEPTH_BITS
        2931,   // DEPTH_CLEAR_VALUE
        2932,   // DEPTH_FUNC
        2928,   // DEPTH_RANGE
        2929,   // DEPTH_TEST
        2930,   // DEPTH_WRITEMASK
        3024,   // DITHER
        34965,  // ELEMENT_ARRAY_BUFFER_BINDING
        36006,  // FRAMEBUFFER_BINDING
        2886,   // FRONT_FACE
        33170,  // GENERATE_MIPMAP_HINT
        3411,   // GREEN_BITS
        35739,  // IMPLEMENTATION_COLOR_READ_FORMAT
        35738,  // IMPLEMENTATION_COLOR_READ_TYPE
        2849,   // LINE_WIDTH
        35661,  // MAX_COMBINED_TEXTURE_IMAGE_UNITS
        34076,  // MAX_CUBE_MAP_TEXTURE_SIZE
        36349,  // MAX_FRAGMENT_UNIFORM_VECTORS
        34024,  // MAX_RENDERBUFFER_SIZE
        34930,  // MAX_TEXTURE_IMAGE_UNITS
        3379,   // MAX_TEXTURE_SIZE
        36348,  // MAX_VARYING_VECTORS
        34921,  // MAX_VERTEX_ATTRIBS
        35660,  // MAX_VERTEX_TEXTURE_IMAGE_UNITS
        36347,  // MAX_VERTEX_UNIFORM_VECTORS
        3386,   // MAX_VIEWPORT_DIMS
        3333,   // PACK_ALIGNMENT
        32824,  // POLYGON_OFFSET_FACTOR
        32823,  // POLYGON_OFFSET_FILL
        10752,  // POLYGON_OFFSET_UNITS
        3410,   // RED_BITS
        36007,  // RENDERBUFFER_BINDING
        7937,   // RENDERER
        32936,  // SAMPLE_BUFFERS
        32939,  // SAMPLE_COVERAGE_INVERT
        32938,  // SAMPLE_COVERAGE_VALUE
        32937,  // SAMPLES
        3088,   // SCISSOR_BOX
        3089,   // SCISSOR_TEST
        35724,  // SHADING_LANGUAGE_VERSION
        34817,  // STENCIL_BACK_FAIL
        34816,  // STENCIL_BACK_FUNC
        34818,  // STENCIL_BACK_PASS_DEPTH_FAIL
        34819,  // STENCIL_BACK_PASS_DEPTH_PASS
        36003,  // STENCIL_BACK_REF
        36004,  // STENCIL_BACK_VALUE_MASK
        36005,  // STENCIL_BACK_WRITEMASK
        3415,   // STENCIL_BITS
        2961,   // STENCIL_CLEAR_VALUE
        2964,   // STENCIL_FAIL
        2962,   // STENCIL_FUNC
        2965,   // STENCIL_PASS_DEPTH_FAIL
        2966,   // STENCIL_PASS_DEPTH_PASS
        2967,   // STENCIL_REF
        2960,   // STENCIL_TEST
        2963,   // STENCIL_VALUE_MASK
        2968,   // STENCIL_WRITEMASK
        3408,   // SUBPIXEL_BITS
        32873,  // TEXTURE_BINDING_2D
        34068,  // TEXTURE_BINDING_CUBE_MAP
        3317,   // UNPACK_ALIGNMENT
        37443,  // UNPACK_COLORSPACE_CONVERSION_WEBGL
        37440,  // UNPACK_FLIP_Y_WEBGL
        37441,  // UNPACK_PREMULTIPLY_ALPHA_WEBGL
        7936,   // VENDOR
        7938,   // VERSION
        2978,   // VIEWPORT
    ];
    // https://registry.khronos.org/webgl/specs/2.0/#3.7.2
    const webgl2Enums = [
        ...webgl1Enums, // INCLUDES ALL WEBGL 1.0 ENUMS
        36662,  // COPY_READ_BUFFER_BINDING
        36663,  // COPY_WRITE_BUFFER_BINDING
        35723,  // FRAGMENT_SHADER_DERIVATIVE_HINT
        32883,  // MAX_3D_TEXTURE_SIZE
        35071,  // MAX_ARRAY_TEXTURE_LAYERS
        37447,  // MAX_CLIENT_WAIT_TIMEOUT_WEBGL
        36063,  // MAX_COLOR_ATTACHMENTS
        35379,  // MAX_COMBINED_FRAGMENT_UNIFORM_COMPONENTS
        35374,  // MAX_COMBINED_UNIFORM_BLOCKS
        35377,  // MAX_COMBINED_VERTEX_UNIFORM_COMPONENTS
        34852,  // MAX_DRAW_BUFFERS
        36203,  // MAX_ELEMENT_INDEX
        33001,  // MAX_ELEMENTS_INDICES
        33000,  // MAX_ELEMENTS_VERTICES
        37157,  // MAX_FRAGMENT_INPUT_COMPONENTS
        35373,  // MAX_FRAGMENT_UNIFORM_BLOCKS
        35657,  // MAX_FRAGMENT_UNIFORM_COMPONENTS
        35077,  // MAX_PROGRAM_TEXEL_OFFSET
        36183,  // MAX_SAMPLES
        37137,  // MAX_SERVER_WAIT_TIMEOUT
        34045,  // MAX_TEXTURE_LOD_BIAS
        35978,  // MAX_TRANSFORM_FEEDBACK_INTERLEAVED_COMPONENTS
        35979,  // MAX_TRANSFORM_FEEDBACK_SEPARATE_ATTRIBS
        35968,  // MAX_TRANSFORM_FEEDBACK_SEPARATE_COMPONENTS
        35376,  // MAX_UNIFORM_BLOCK_SIZE
        35375,  // MAX_UNIFORM_BUFFER_BINDINGS
        35659,  // MAX_VARYING_COMPONENTS
        37154,  // MAX_VERTEX_OUTPUT_COMPONENTS
        35371,  // MAX_VERTEX_UNIFORM_BLOCKS
        35658,  // MAX_VERTEX_UNIFORM_COMPONENTS
        35076,  // MIN_PROGRAM_TEXEL_OFFSET
        3330,   // PACK_ROW_LENGTH
        3332,   // PACK_SKIP_PIXELS
        3331,   // PACK_SKIP_ROWS
        35053,  // PIXEL_PACK_BUFFER_BINDING
        35055,  // PIXEL_UNPACK_BUFFER_BINDING
        35977,  // RASTERIZER_DISCARD
        3074,   // READ_BUFFER
        36010,  // READ_FRAMEBUFFER_BINDING
        32926,  // SAMPLE_ALPHA_TO_COVERAGE
        32928,  // SAMPLE_COVERAGE
        35097,  // SAMPLER_BINDING
        35869,  // TEXTURE_BINDING_2D_ARRAY
        32938,  // TEXTURE_BINDING_3D
        36388,  // TRANSFORM_FEEDBACK_ACTIVE
        36389,  // TRANSFORM_FEEDBACK_BINDING / TRANSFORM_FEEDBACK_BUFFER_BINDING
        36387,  // TRANSFORM_FEEDBACK_PAUSED
        35368,  // UNIFORM_BUFFER_BINDING
        35380,  // UNIFORM_BUFFER_OFFSET_ALIGNMENT
        32878,  // UNPACK_IMAGE_HEIGHT
        3314,   // UNPACK_ROW_LENGTH
        32877,  // UNPACK_SKIP_IMAGES
        3316,   // UNPACK_SKIP_PIXELS
        3315,   // UNPACK_SKIP_ROWS
        34229,  // VERTEX_ARRAY_BINDING
    ];

    const glEnums = webglContextId === 'webgl' ? webgl1Enums : webgl2Enums;

    let rendererInfo = null
    let supportedProfile = null
    let performanceIssueDetected = false

    // const perfCanvas = new OffscreenCanvas(256, 256)
    let perfCanvas = document.createElement("canvas");
    perfCanvas.width = 256;
    perfCanvas.height = 256;
    const perfCtx = perfCanvas.getContext('webgl', {failIfMajorPerformanceCaveat: true});

    if (!perfCtx) {
        performanceIssueDetected = true
    }

    try {
        rendererInfo = {
            webglVendor: webglContext.getParameter(webglContext.VENDOR),
            webglRenderer: webglContext.getParameter(webglContext.RENDERER),
            version: webglContext.getParameter(webglContext.VERSION),
        }
    } catch (e) {
        pushCollectIncident(incidents, { source: webglContextId, phase: 'rendererInfo', error: e });
    }

    let unmaskedRendererInfo = null;
    try {
        const dbgExt = webglContext.getExtension('WEBGL_debug_renderer_info');
        if (dbgExt) {
            unmaskedRendererInfo = {
                webglVendor: webglContext.getParameter(dbgExt.UNMASKED_VENDOR_WEBGL),
                webglRenderer: webglContext.getParameter(dbgExt.UNMASKED_RENDERER_WEBGL),
            };
        }
    } catch (e) {
        pushCollectIncident(incidents, { source: webglContextId, phase: 'unmaskedRenderer', error: e });
    }

    const astcExt = webglContext.getExtension('WEBGL_compressed_texture_astc');
    if (astcExt && typeof astcExt.getSupportedProfiles === 'function') {
        try {
            supportedProfile = astcExt.getSupportedProfiles().toString();
        } catch (e) {
            /* extension present but profile query failed */
        }
    }

    let supportedExtensions = [];
    try {
        supportedExtensions = webglContext.getSupportedExtensions() || [];
    } catch (e) {
        pushCollectIncident(incidents, { source: webglContextId, phase: 'getSupportedExtensions', error: e });
    }

    let contextAttributes = null;
    try {
        contextAttributes = webglContext.getContextAttributes();
    } catch (e) {
        pushCollectIncident(incidents, { source: webglContextId, phase: 'getContextAttributes', error: e });
    }

    let maxAnisotropy = null;
    try {
        maxAnisotropy = getMaxAnisotropy(webglContext);
    } catch (e) {
        pushCollectIncident(incidents, { source: webglContextId, phase: 'maxAnisotropy', error: e });
    }

    const result = {
        'rendererInfo': rendererInfo,
        'unmaskedRendererInfo': unmaskedRendererInfo,
        'supportedProfile': supportedProfile,
        'performanceIssue': performanceIssueDetected,
        'supportedExtensions': supportedExtensions,
        'contextAttributes': contextAttributes,
        'maxAnisotropy': maxAnisotropy,
        'params': {},
        'incidents': incidents,
    };

    let paramsLoopError = null;
    for (const glEnum of glEnums) {
        try {
            const parmValue = webglContext.getParameter(glEnum);
            result.params[glEnum] = {
                'type': parmValue ? parmValue.constructor.name : '',
                'value': parmValue,
            };
        } catch (err) {
            if (!paramsLoopError) paramsLoopError = err;
        }
    }
    if (paramsLoopError) {
        pushCollectIncident(incidents, { source: webglContextId, phase: 'params', error: paramsLoopError });
    }

    //
    const args = [
        [webglContext.VERTEX_SHADER, webglContext.HIGH_FLOAT],
        [webglContext.VERTEX_SHADER, webglContext.MEDIUM_FLOAT],
        [webglContext.VERTEX_SHADER, webglContext.LOW_FLOAT],
        [webglContext.VERTEX_SHADER, webglContext.HIGH_INT],
        [webglContext.VERTEX_SHADER, webglContext.MEDIUM_INT],
        [webglContext.VERTEX_SHADER, webglContext.LOW_INT],

        [webglContext.FRAGMENT_SHADER, webglContext.HIGH_FLOAT],
        [webglContext.FRAGMENT_SHADER, webglContext.MEDIUM_FLOAT],
        [webglContext.FRAGMENT_SHADER, webglContext.LOW_FLOAT],
        [webglContext.FRAGMENT_SHADER, webglContext.HIGH_INT],
        [webglContext.FRAGMENT_SHADER, webglContext.MEDIUM_INT],
        [webglContext.FRAGMENT_SHADER, webglContext.LOW_INT],
    ];

    result.shaderPrecisionFormats = [];
    let shaderPrecisionError = null;
    for (let arg of args) {
        let [shaderType, precisionType] = arg;
        let r = null;
        try {
            r = webglContext.getShaderPrecisionFormat(shaderType, precisionType);
        } catch (e) {
            if (!shaderPrecisionError) shaderPrecisionError = e;
        }

        result.shaderPrecisionFormats.push({
            shaderType,
            precisionType,
            r: r
                ? {
                    rangeMin: r.rangeMin,
                    rangeMax: r.rangeMax,
                    precision: r.precision,
                }
                : null,
        });
    }
    if (shaderPrecisionError) {
        pushCollectIncident(incidents, { source: webglContextId, phase: 'shaderPrecision', error: shaderPrecisionError });
    }

    releaseWebGLContext(webglContext);
    releaseWebGLContext(perfCtx);
    return result;
};

function releaseWebGLContext(ctx) {
    try {
        const ext = ctx && typeof ctx.getExtension === 'function' ? ctx.getExtension('WEBGL_lose_context') : null;
        if (ext) ext.loseContext();
    } catch (e) {
        /* ignore */
    }
}

export { dumpWebGLCore }